import React from 'react';
import { Star, Quote, MapPin } from 'lucide-react';
import { Avatar, AvatarFallback } from './ui/avatar';
import { testimonials } from '../data/mock';

const Testimonials = () => {
  const getInitials = (name) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase();

  return (
    <section id="testimonials" className="py-8 md:py-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
          <div className="inline-flex items-center space-x-2 bg-cyan-100 text-cyan-700 rounded-full px-4 py-2 mb-4">
            <Star size={18} />
            <span className="text-sm font-semibold">Témoignages</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Ce que disent{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-pink-500 bg-clip-text text-transparent">
              nos clients
            </span>
          </h2>
        </div>

        {/* Testimonials - Compact horizontal scroll on mobile, grid on desktop */}
        <div className="flex md:hidden gap-3 overflow-x-auto pb-4 snap-x snap-mandatory -mx-4 px-4 scrollbar-hide">
          {testimonials.slice(0, 6).map((t) => (
            <div
              key={t.id}
              className="snap-start flex-shrink-0 w-[280px] bg-white rounded-xl shadow-md p-4 relative"
            >
              <div className="flex space-x-0.5 mb-2">
                {[...Array(t.rating)].map((_, i) => (
                  <Star key={i} className="fill-yellow-400 text-yellow-400" size={12} />
                ))}
              </div>
              <p className="text-gray-700 text-xs leading-relaxed mb-3 line-clamp-3">"{t.text}"</p>
              <div className="flex items-center space-x-2 pt-2 border-t border-gray-100">
                <Avatar className="w-7 h-7 bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-[10px]">
                  <AvatarFallback className="text-[10px]">{getInitials(t.name)}</AvatarFallback>
                </Avatar>
                <div>
                  <h4 className="font-bold text-gray-900 text-xs">{t.name}</h4>
                  <div className="flex items-center space-x-1 text-[10px] text-gray-500">
                    <MapPin size={10} />
                    <span>{t.location}</span>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-pink-500 rounded-b-xl"></div>
            </div>
          ))}
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 mb-10">
          {testimonials.slice(0, 6).map((t, index) => (
            <div
              key={t.id}
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="absolute top-4 right-4 text-cyan-200">
                <Quote size={28} />
              </div>
              <div className="flex space-x-1 mb-3">
                {[...Array(t.rating)].map((_, i) => (
                  <Star key={i} className="fill-yellow-400 text-yellow-400" size={16} />
                ))}
              </div>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">"{t.text}"</p>
              <div className="flex items-center space-x-3 pt-3 border-t border-gray-100">
                <Avatar className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-xs">
                  <AvatarFallback className="text-xs">{getInitials(t.name)}</AvatarFallback>
                </Avatar>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">{t.name}</h4>
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <MapPin size={12} />
                    <span>{t.location}</span>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-pink-500 rounded-b-xl"></div>
            </div>
          ))}
        </div>

        {/* Trust Indicators - Compact */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10 max-w-3xl mx-auto">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">4.9/5</div>
              <div className="text-xs md:text-sm text-gray-600">Note moyenne</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">500+</div>
              <div className="text-xs md:text-sm text-gray-600">Avis positifs</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">100%</div>
              <div className="text-xs md:text-sm text-gray-600">Satisfaction</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
